"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  label: "§ 05.1 — SUMMER ENERGY",
  value: 450,
  suffix: "",
  unit: "CAMPERS · ANNUAL ATTENDANCE",
  note: "High-energy summer camp sessions fostering growth and connection.",
  color: "cobalt"
}, {
  label: "§ 05.2 — COMMUNITY IMPACT",
  value: 1200,
  suffix: "",
  unit: "HOURS · VOLUNTEER PROJECTS",
  note: "Youth-led initiatives serving our local neighborhoods and parks.",
  color: "cyan"
}, {
  label: "§ 05.3 — WEEKLY SESSIONS",
  value: 52,
  suffix: "",
  unit: "EVENTS · FRIDAY NIGHTS",
  note: "Consistent social gatherings, music nights, and team sports.",
  color: "cobalt"
}, {
  label: "§ 05.4 — SMALL GROUPS",
  value: 28,
  suffix: "",
  unit: "CIRCLES · MENTORSHIP TEAMS",
  note: "Deep-dive discussions and peer support networks for every member.",
  color: "cyan"
}];
const tickerItems = ["TRIBE-2024", "CAMP-SUMMER", "FRIDAY-NIGHTS", "TEAM-SPORTS", "LIVE-MUSIC", "VOLUNTEER-PROJECTS", "MENTORSHIP", "YOUTH-TRIBE", "COMMUNITY", "GROWTH", "CONNECTION", "ENERGY"];
function useCountUp(target: number, start: boolean, duration = 1200) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!start || startedRef.current) return;
    startedRef.current = true;
    const startTime = performance.now();
    const ease = (t: number) => {
      return 1 - Math.pow(1 - t, 3);
    };
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      setValue(Math.round(target * ease(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}
function StatBlock({
  stat,
  index,
  triggered
}: {
  stat: (typeof stats)[number];
  index: number;
  triggered: boolean;
}) {
  const animated = useCountUp(stat.value, triggered);
  const colorStyle = stat.color === "cobalt" ? {
    color: "hsl(226 91% 39%)"
  } : {
    color: "hsl(190 100% 40%)"
  };
  return <div data-index={index} className="relative flex flex-col gap-4 px-0 py-8 md:py-0 md:px-8 first:md:pl-0 last:md:pr-0">
      <div className="font-default uppercase" style={{
      fontSize: "0.75rem",
      letterSpacing: "0.3em",
      fontWeight: 500,
      color: "hsl(226 10% 45%)"
    }}>
        
        <span style={{
        color: "hsl(226 10% 45%)"
      }}>
          {" "}— {stat.label.split("—")[1].trim()}
        </span>
      </div>

      <div className="font-default" style={{
      fontSize: "clamp(3.5rem, 7.5vw, 7.5rem)",
      fontWeight: 800,
      letterSpacing: "-0.04em",
      lineHeight: 0.95,
      ...colorStyle
    }}>
        {animated.toLocaleString()}
      </div>

      <div className="font-default uppercase" style={{
      fontSize: "0.75rem",
      letterSpacing: "0.05em",
      fontWeight: 600,
      color: "hsl(226 60% 8%)"
    }}>
        {stat.unit}
      </div>

      <p className="font-default" style={{
      fontSize: "1rem",
      lineHeight: 1.5,
      color: "hsl(226 10% 45%)",
      maxWidth: "28ch"
    }}>
        {stat.note}
      </p>
    </div>;
}
export default function ImpactByTheNumbers() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  return <section data-section-id="2115" id="impact-by-the-numbers" className="relative overflow-hidden bg-background text-foreground">
      {}
      <div className="relative w-full overflow-hidden border-b border-border" style={{
      backgroundColor: "hsl(0 0% 100%)"
    }}>
        <div className="flex whitespace-nowrap animate-[ticker_60s_linear_infinite] py-3">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="font-default inline-flex items-center" style={{
          fontSize: "0.75rem",
          letterSpacing: "0.05em",
          fontWeight: 600,
          color: "hsl(226 10% 45%)"
        }}>
              <span className="px-6">{item}</span>
              <span className="inline-block h-1 w-1" style={{
            backgroundColor: "hsl(226 91% 39%)"
          }} aria-hidden="true" />
            </span>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>

      <div ref={sectionRef} className="relative mx-auto w-full max-w-[1440px] px-5 md:px-8 lg:px-14 py-28 md:py-36">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/3 select-none font-default hidden md:block" style={{
        fontSize: "860px",
        lineHeight: 1,
        fontWeight: 900,
        WebkitTextStroke: "1px hsl(226 91% 39%)",
        color: "transparent",
        opacity: 0.05
      }}>
          05
        </div>

        {}
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {}
          <div className="lg:col-span-7 flex flex-col gap-7">
            {}
            <div className="font-default uppercase" style={{
            fontSize: "0.75rem",
            letterSpacing: "0.3em",
            fontWeight: 500
          }}>
              
              <span style={{
              color: "hsl(226 10% 45%)"
            }}>
                {" "}— TRIBE IMPACT / ANNUAL METRICS
              </span>
            </div>

            {}
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-1.5">
                <span className="inline-block h-1 w-1" style={{
                backgroundColor: "hsl(226 91% 39%)"
              }} />
                <span className="inline-block h-1 w-1" style={{
                backgroundColor: "hsl(226 91% 39%)"
              }} />
                <span className="inline-block h-1 w-1" style={{
                backgroundColor: "hsl(226 91% 39%)"
              }} />
              </div>
            </div>

            {}
            <h2 className="font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            lineHeight: 1.02,
            color: "hsl(226 60% 8%)"
          }}>
              Building a <span style={{
              fontStyle: "italic"
            }}>stronger</span> tribe together.
            </h2>
          </div>

          {}
          <div className="lg:col-span-5 flex flex-col justify-end gap-6">
            <p className="font-default" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.6,
            color: "hsl(226 10% 45%)"
          }}>
              From summer camp memories to weekly volunteer projects, our impact is measured by the connections we build and the lives we touch every single day.
            </p>

            <Link to="/ParentHubPage" className="group inline-flex items-center gap-2 self-start font-default" style={{
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "hsl(226 91% 39%)"
          }}>
              <span className="relative inline-block">
                Download Registration
                <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 transition-transform duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover:scale-x-100" style={{
                backgroundColor: "hsl(226 91% 39%)"
              }} aria-hidden="true" />
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {}
        <div className="mt-20 mb-12 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-1 w-1" style={{
            backgroundColor: "hsl(226 91% 39%)"
          }} />
            <span className="inline-block h-1 w-1" style={{
            backgroundColor: "hsl(226 91% 39%)"
          }} />
            <span className="inline-block h-1 w-1" style={{
            backgroundColor: "hsl(226 91% 39%)"
          }} />
          </div>
        </div>

        {}
        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {stats.map((stat, i) => <div data-index={i} key={i} className={`relative ${i > 0 ? "lg:border-l" : ""} ${i % 2 === 1 ? "sm:border-l lg:border-l" : ""} ${i >= 2 ? "sm:border-t lg:border-t-0" : ""} border-t sm:border-t-0 first:border-t-0 border-border`}>
              <StatBlock stat={stat} index={i} triggered={isVisible} />
            </div>)}
        </div>
      </div>

      {}
      <div className="relative w-full overflow-hidden border-t border-border" style={{
      backgroundColor: "hsl(0 0% 100%)"
    }}>
        <div className="flex whitespace-nowrap animate-[ticker_60s_linear_infinite] py-3">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="font-default inline-flex items-center" style={{
          fontSize: "0.75rem",
          letterSpacing: "0.05em",
          fontWeight: 600,
          color: "hsl(226 10% 45%)"
        }}>
              <span className="px-6">{item}</span>
              <span className="inline-block h-1 w-1" style={{
            backgroundColor: "hsl(226 91% 39%)"
          }} aria-hidden="true" />
            </span>)}
        </div>
      </div>
    </section>;
}